import Dexie, { type Table } from 'dexie';
import type {
  BodyWeight,
  DayRecord,
  Exercise,
  Food,
  MealEntry,
  Profile,
  RoutineTemplate,
  SavedMeal,
  Workout,
} from '@/engine/types';

/**
 * Campos de sincronizacion presentes en toda fila (SPEC 3.2).
 * `deletedAt` es borrado logico: nunca se borra una fila de verdad, porque en
 * offline-first un DELETE real no se puede propagar si el otro dispositivo
 * nunca vio la fila.
 */
export interface SyncFields {
  updatedAt: string;
  deletedAt: string | null;
}

export type Row<T> = T & SyncFields;

export type ProfileRow = Row<Profile> & { id: 'me' };
export type FoodRow = Row<Food>;
export type MealRow = Row<MealEntry>;
export type DayRow = Row<DayRecord>;
export type BodyWeightRow = Row<BodyWeight>;
export type ExerciseRow = Row<Exercise>;
export type WorkoutRow = Row<Workout>;
export type RoutineRow = Row<RoutineTemplate>;
export type SavedMealRow = Row<SavedMeal>;

/** Tablas que se sincronizan con Supabase. El orden importa al subir. */
export const SYNCED_TABLES = [
  'profile',
  'foods',
  'meals',
  'days',
  'bodyWeights',
  'exercises',
  'workouts',
  'routines',
  'savedMeals',
] as const;

export type SyncedTable = (typeof SYNCED_TABLES)[number];

export interface OutboxItem {
  seq?: number;
  table: SyncedTable;
  /** Clave primaria de la fila afectada. */
  key: string;
  /** La fila completa. Gana la ultima escritura, asi que basta el estado final. */
  payload: unknown;
  queuedAt: string;
  tries: number;
  lastError?: string;
}

export interface MetaItem {
  key: string;
  value: unknown;
}

class NutriGymDB extends Dexie {
  profile!: Table<ProfileRow, string>;
  foods!: Table<FoodRow, string>;
  meals!: Table<MealRow, string>;
  days!: Table<DayRow, string>;
  bodyWeights!: Table<BodyWeightRow, string>;
  exercises!: Table<ExerciseRow, string>;
  workouts!: Table<WorkoutRow, string>;
  routines!: Table<RoutineRow, string>;
  savedMeals!: Table<SavedMealRow, string>;
  outbox!: Table<OutboxItem, number>;
  meta!: Table<MetaItem, string>;

  constructor() {
    super('nutrigym');

    this.version(1).stores({
      profile: 'id, updatedAt',
      // `name` y `barcode` indexados para la busqueda local y el escaner.
      foods: 'id, kind, name, barcode, updatedAt, deletedAt',
      // [date+slot] permite pintar el panel del dia sin recorrer todo.
      meals: 'id, date, [date+slot], foodId, updatedAt, deletedAt',
      days: 'date, complete, updatedAt',
      bodyWeights: 'date, updatedAt, deletedAt',
      exercises: 'id, pattern, custom, updatedAt, deletedAt',
      workouts: 'id, date, updatedAt, deletedAt',
      outbox: '++seq, table, key',
      meta: 'key',
    });

    // v2: plantillas de rutina. Dexie conserva los datos de la v1 al migrar.
    this.version(2).stores({
      routines: 'id, name, updatedAt, deletedAt',
    });

    // Comidas guardadas (SPEC 6.B). Version nueva y no un cambio de la 2: una
    // base ya creada en un movil tiene que migrar, no romperse.
    this.version(3).stores({
      savedMeals: 'id, name, updatedAt, deletedAt',
    });
  }
}

export const db = new NutriGymDB();

/** Marca de tiempo de sincronizacion. Siempre UTC. */
export function nowIso(): string {
  return new Date().toISOString();
}

export function newId(): string {
  return crypto.randomUUID();
}

/**
 * Escribe en local y encola la subida, en una sola transaccion: si algo falla,
 * no queda un cambio guardado que nunca se subira ni una subida de algo que no
 * se guardo.
 */
export async function putAndQueue<T extends object>(
  table: SyncedTable,
  key: string,
  row: T,
): Promise<void> {
  await db.transaction('rw', db[table], db.outbox, async () => {
    await (db[table] as unknown as Table<T, string>).put(row);

    // Un registro editado cinco veces sin cobertura ocupa UNA entrada, no
    // cinco: como gana la última escritura, solo importa el estado final. Sin
    // esto la cola crece sin límite mientras no haya nube configurada.
    //
    // La entrada vieja se BORRA y la nueva lleva `seq` nuevo. Si se reutilizara
    // el `seq`, una subida en curso con la versión vieja borraría al terminar
    // esta edición, que aún no ha subido (se perdía en silencio).
    const existing = await db.outbox.where('key').equals(key).toArray();
    const mine = existing.filter((item) => item.table === table).map((item) => item.seq!);
    await db.outbox.bulkDelete(mine);

    await db.outbox.add({ table, key, payload: row, queuedAt: nowIso(), tries: 0 });
  });
}

/** Borrado logico: marca `deletedAt` y encola. */
export async function softDeleteAndQueue(table: SyncedTable, key: string): Promise<void> {
  const target = db[table] as unknown as Table<Record<string, unknown>, string>;
  const existing = await target.get(key);
  if (!existing) return;
  const row = { ...existing, deletedAt: nowIso(), updatedAt: nowIso() };
  await putAndQueue(table, key, row);
}

/**
 * Gana la ultima escritura por registro (SPEC 3.2). Se compara `updatedAt`;
 * no hay merge campo a campo a proposito: con un grupo de 10 personas y un
 * dispositivo por cabeza, la complejidad no se paga.
 */
export function winsRemote(localUpdatedAt: string, remoteUpdatedAt: string): boolean {
  // Se comparan instantes, no textos: Postgres devuelve `10:00:00.5+00:00`
  // para lo que aquí se guardó como `10:00:00.500Z`.
  return isoMs(remoteUpdatedAt) > isoMs(localUpdatedAt);
}

/**
 * Milisegundos de una fecha ISO tal como llega de Postgres. Se recortan los
 * microsegundos a 3 decimales: el formato de `Date.parse` no garantiza más, y
 * en Safari es justo donde falla lo que no está garantizado.
 */
export function isoMs(iso: string): number {
  if (!iso) return Number.NEGATIVE_INFINITY;
  return Date.parse(iso.replace(/\.(\d{1,3})\d*/, (_, ms: string) => `.${ms.padEnd(3, '0')}`));
}
